import { useEffect, useRef, useState, useCallback } from 'react'

export interface ProgressMessage {
  type: 'progress' | 'pong'
  step?: number
  total?: number
  message?: string
  percentage?: number
  elapsed_seconds?: number
}

interface UseWebSocketOptions {
  onMessage?: (data: ProgressMessage) => void
  onOpen?: () => void
  onClose?: () => void
  onError?: (error: Event) => void
  reconnectInterval?: number
  maxReconnectAttempts?: number
}

export const useWebSocket = (
  url: string | null,
  options: UseWebSocketOptions = {}
) => {
  const {
    onMessage,
    onOpen,
    onClose,
    onError,
    reconnectInterval = 3000,
    maxReconnectAttempts = 5,
  } = options

  const [isConnected, setIsConnected] = useState(false)
  const [reconnectAttempts, setReconnectAttempts] = useState(0)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const connect = useCallback(() => {
    if (!url || wsRef.current?.readyState === WebSocket.OPEN) {
      return
    }

    try {
      // Crear WebSocket
      const ws = new WebSocket(url)

      ws.onopen = () => {
        console.log('[WS] Conectado')
        setIsConnected(true)
        setReconnectAttempts(0)
        onOpen?.()

        // Enviar ping cada 30 segundos para mantener conexión viva
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping')
          }
        }, 30000)
      }

      ws.onmessage = (event) => {
        try {
          const data: ProgressMessage = JSON.parse(event.data)
          onMessage?.(data)
        } catch (error) {
          console.error('[WS] Error parseando mensaje:', error)
        }
      }

      ws.onerror = (error) => {
        console.error('[WS] Error:', error)
        onError?.(error)
      }

      ws.onclose = () => {
        console.log('[WS] Desconectado')
        setIsConnected(false)
        onClose?.()

        // Limpiar ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current)
          pingIntervalRef.current = null
        }

        // Intentar reconectar si no se alcanzó el límite
        if (reconnectAttempts < maxReconnectAttempts) {
          console.log(`[WS] Reintentando conexión en ${reconnectInterval}ms (intento ${reconnectAttempts + 1}/${maxReconnectAttempts})`)
          reconnectTimeoutRef.current = setTimeout(() => {
            setReconnectAttempts((prev) => prev + 1)
            connect()
          }, reconnectInterval)
        } else {
          console.error('[WS] Máximo de reintentos alcanzado')
        }
      }

      wsRef.current = ws
    } catch (error) {
      console.error('[WS] Error creando conexión:', error)
    }
  }, [url, onMessage, onOpen, onClose, onError, reconnectAttempts, maxReconnectAttempts, reconnectInterval])

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current)
      pingIntervalRef.current = null
    }

    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }

    setIsConnected(false)
  }, [])

  const sendMessage = useCallback((message: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(message)
    } else {
      console.warn('[WS] No se puede enviar mensaje: WebSocket no conectado')
    }
  }, [])

  useEffect(() => {
    if (url) {
      connect()
    }

    return () => {
      disconnect()
    }
  }, [url, connect, disconnect])

  return {
    isConnected,
    sendMessage,
    disconnect,
    reconnect: connect,
  }
}

export default useWebSocket
